import React, { useEffect, useState } from "react";
import { Shield } from "lucide-react";
import GalleryLightbox from "../components/GalleryLightbox";
import { useLanguage } from "../components/LanguageContext";
import SEO from "../components/SEO";

export default function GalleryPage() {
  const { lang, t } = useLanguage();
  const [galleries, setGalleries] = useState([]);
  const [selectedGallery, setSelectedGallery] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchGalleries = async () => {
      try {
        const response = await fetch("https://api.dev.auxo.dk/public/v2/content/gallery");
        if (!response.ok) {
          throw new Error(lang === "da" ? "Kunne ikke hente gallerier" : "Could not fetch galleries");
        }
        const data = await response.json();
        setGalleries(data.items || []);
        if (data.items && data.items.length > 0) {
          setSelectedGallery(data.items[0]);
        }
      } catch (err) {
        console.error("Fejl ved hentning af gallerier:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchGalleries();
  }, [lang]);

  const getGalleryTitle = (gal) => {
    if (!gal) return "";
    if (lang === "da") return gal.title;
    // Translate gallery titles for English users based on gallery_slug
    if (gal.gallery_slug === "salg-galleri") return "Sales Gallery";
    if (gal.gallery_slug === "hovedgalleri" || gal.gallery_slug === "equinesport") return "Equinesport";
    if (gal.gallery_slug === "sportsheste-galleri" || gal.gallery_slug === "staevner") return "Competitions";
    return gal.title;
  };

  return (
    <div className="bg-[#FCF9F5] text-stone-900 py-24 font-sans-custom">
      <SEO pageKey="gallery" />

      <div className="mx-auto max-w-7xl px-6 md:px-12">
        {/* Header */}
        <div className="max-w-3xl space-y-6 mb-10 md:mb-16 lg:mb-20">
          <div className="inline-flex items-center gap-3 border border-stone-300 bg-stone-100 px-4 py-1.5 text-[10px] uppercase tracking-[0.3em] text-stone-800 rounded-none font-medium font-sans-custom">
            <Shield className="h-3.5 w-3.5 text-stone-700" />
            <span>{t.gallery.badge}</span>
          </div>
          <h1 className="font-display text-3xl sm:text-5xl md:text-6xl uppercase tracking-normal sm:tracking-wide-custom text-stone-900 font-medium">
            {t.gallery.title}{" "}
            <span className="italic text-stone-600 lowercase font-display normal-case">
              {t.gallery.title_italic}
            </span>
          </h1>
          <p className="text-sm text-stone-600 leading-relaxed font-light tracking-wide-custom font-sans-custom">
            {t.gallery.desc}
          </p>
        </div>

        {/* Loading / Error States */}
        {loading ? (
          <div className="flex h-64 items-center justify-center">
            <div className="h-8 w-8 animate-spin border-2 border-stone-800 border-t-transparent rounded-none" />
          </div>
        ) : error ? (
          <div className="border border-red-200 bg-red-50/50 p-6 text-center text-red-600 rounded-none">
            {t.gallery.error}: {error}.
          </div>
        ) : galleries.length === 0 ? (
          <div className="border border-stone-200 bg-white p-12 text-center text-stone-500 rounded-none shadow-sm">
            {t.gallery.empty}
          </div>
        ) : (
          <div className="space-y-12">
            {/* Gallery Selector Tabs */}
            <div className="flex flex-wrap gap-3 border-b border-stone-200 pb-6">
              {galleries.map((gal) => (
                <button
                  key={gal.id}
                  onClick={() => setSelectedGallery(gal)}
                  className={`px-3.5 py-2.5 sm:px-5 sm:py-3 text-[8px] sm:text-[10px] uppercase tracking-widest transition-all duration-300 rounded-none font-semibold cursor-pointer font-sans-custom ${
                    selectedGallery?.id === gal.id
                      ? "bg-stone-800 text-stone-50"
                      : "border border-stone-200 bg-white text-stone-500 hover:border-stone-800 hover:text-stone-900"
                  }`}
                >
                  {getGalleryTitle(gal)}
                </button>
              ))}
            </div>

            {/* Lightbox Gallery */}
            {selectedGallery && (
              <div className="space-y-8">
                <h2 className="font-display text-2xl sm:text-3xl text-stone-900 uppercase tracking-normal sm:tracking-wide-custom font-medium">
                  {getGalleryTitle(selectedGallery)}
                </h2>
                <GalleryLightbox
                  images={selectedGallery.images || []}
                  emptyMessage={t.gallery.empty_album}
                />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
